import React from 'react';
import {DisposableObject} from "../../../../utils/disposable-object";
import {IGamesFilterStrategy} from "./games-filter-strategy.interface";
import {GameInfo} from "../../games/game-info";
import {computed, observable, runInAction} from "mobx";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {Nullable} from "../../../../common/types/nullable-types";
import {GamesMap} from "../../games/games.service.interface";
import {IArenaGameFiltersCategory} from "../arena-filters/arena-game-filters-category.interface";
import {IArenaGameFilter} from "../arena-filters/arena-game-filter.interface";
import {ApplyGameFilterResult, IGameFilterEvaluator} from "../interfaces/game-filter-evaluator.interface";
import {IPcFilterIndicatorRenderingOptions} from "../games-filter.service.interface";

export abstract class GamesFilterStrategyBase extends DisposableObject implements IGamesFilterStrategy {

    constructor(protected readonly services: IServiceFactoryExtended) {
        super();

        this.addReaction(() => [this.services.location.currentUri, this.services.games.gamesMap], () => {
            //We use set timeout in order to avoid a circular dependency error.
            //Because this reaction is with fireImmediately=true it executes in this constructor, but this constructor is called from the GamesFilterService constructor
            //and the code inside this._filterGames is trying to access methods and properties from GamesFilterService and the Lazy class will throw an error in this case.
            setTimeout(() => {
                runInAction(() => {
                    this.filteredGamesMap = this._filterGames();
                });
            });

        }, {
            fireImmediately: true
        });
    }

    protected abstract _createFiltersCategories(): IArenaGameFiltersCategory[];
    abstract renderMobileFiltersIndicator(): React.ReactElement;
    abstract renderPCFiltersIndicator(): Nullable<IPcFilterIndicatorRenderingOptions>;

    abstract showFiltersEditor(): void;
    protected abstract _createEvaluator(): IGameFilterEvaluator | null;

    @observable.ref
    filteredGamesMap: Record<string, GameInfo> = {};

    private _filtersCategories: Nullable<IArenaGameFiltersCategory[]> = null;
    protected get filtersCategories(): IArenaGameFiltersCategory[] {
        if(!this._filtersCategories) {
            this._filtersCategories = this._createFiltersCategories();
        }
        return this._filtersCategories;
    }


    @computed public get currentFilters(): IArenaGameFilter[] {
        const result: IArenaGameFilter[] = [];
        this.filtersCategories.forEach(category => {
            category.filters.forEach(f => result.push(f));
        });

        return result;
    }

    get isFilteringAvailable(): boolean {
        return this.currentFilters.length > 0;
    }

    protected _filterGames(): GamesMap {

        const gamesMap = this.services.games.gamesMap;

        if(!this.currentFilters.some(f => f.isApplied)) {
            return gamesMap;
        }

        const evaluator = this._createEvaluator();

        const result: GamesMap = {};

        for(let game of Object.values(this.services.games.gamesMap)) {
            const filterResult = evaluator.applyFilter(game);
            if(ApplyGameFilterResult.IncludeGame === filterResult) {
                result[game.gameType] = game;
            }
        }

        return result;
    }


}